import { useEffect, useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { Alert, Form } from 'antd'
import { createSchemaFieldRule } from 'antd-zod'
import { serviceEnterpriseDraftSchema, type ServiceEnterpriseDraft } from '@super-mega/server/contracts/order'
import { EnterpriseCustomerSelect } from './enterprise-customer-select.tsx'
import { EnterpriseDisplayNameField, EnterpriseDraftEditor } from './enterprise-draft-editor.tsx'
import { enterpriseModalProps } from './enterprise-modal-layout.ts'

const serviceEnterpriseDraftRule = createSchemaFieldRule(serviceEnterpriseDraftSchema)

export function EnterpriseEditorModal({
  open,
  onOpenChange,
  value,
  onChange,
  onConfirm,
  pending,
  customerLocked,
  submitText,
  retainOnCancel = true
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  value: ServiceEnterpriseDraft
  onChange: (value: ServiceEnterpriseDraft) => void
  onConfirm: (draft: ServiceEnterpriseDraft) => Promise<boolean> | boolean
  pending?: boolean
  customerLocked?: boolean
  submitText: string
  retainOnCancel?: boolean
}) {
  const [form] = Form.useForm<ServiceEnterpriseDraft>()
  const [ready, setReady] = useState(true)
  const [error, setError] = useState<string>()
  return (
    <ModalForm<ServiceEnterpriseDraft>
      open={open}
      onOpenChange={onOpenChange}
      form={form}
      initialValues={value}
      title="新建服务对象"
      layout="vertical"
      width="64rem"
      modalProps={{ ...enterpriseModalProps, mask: { closable: false }, closable: !pending, keyboard: !pending }}
      submitter={{
        searchConfig: { submitText, resetText: '取消' },
        submitButtonProps: { loading: pending, disabled: !ready },
        resetButtonProps: { disabled: pending }
      }}
      onFinish={async () => {
        if (!ready) {
          return false
        }
        const parsed = serviceEnterpriseDraftSchema.safeParse(value)
        if (!parsed.success) {
          const issue = parsed.error.issues[0]
          if (issue.path[0] === 'displayName') {
            form.setFields([{ name: 'displayName', errors: [issue.message] }])
          } else {
            setError(issue.message)
          }
          return false
        }
        setError(undefined)
        try {
          return await onConfirm(parsed.data)
        } catch (cause) {
          setError(cause instanceof Error ? cause.message : '保存失败，候选资料已保留')
          return false
        }
      }}
    >
      <EnterpriseDraftFormSync value={value} />
      <p className="mb-4 text-sm text-slate-500">
        {retainOnCancel
          ? '最终确认后才保存正式档案；上传识别仅作预览。'
          : '确认后仅回填本次订单。取消会放弃本次未确认编辑，保留订单原选用；不会创建正式档案。'}
      </p>
      {error && <Alert className="mb-4" type="error" showIcon title={error} />}
      <div className={`mb-4 grid grid-cols-1 items-start gap-4 ${customerLocked ? '' : 'md:grid-cols-2'}`}>
        {!customerLocked && (
          <Form.Item label="所属客户" required className="mb-0!">
            <EnterpriseCustomerSelect
              value={value.customerId || undefined}
              disabled={pending}
              onChange={(customerId) => onChange({ ...value, customerId: customerId ?? 0 })}
            />
          </Form.Item>
        )}
        <EnterpriseDisplayNameField
          value={value}
          onChange={onChange}
          disabled={pending}
          rules={[serviceEnterpriseDraftRule]}
        />
      </div>
      <EnterpriseDraftEditor
        value={value}
        onChange={onChange}
        disabled={pending}
        onReadyChange={setReady}
        hideDisplayName
      />
    </ModalForm>
  )
}

function EnterpriseDraftFormSync({ value }: { value: ServiceEnterpriseDraft }) {
  const form = Form.useFormInstance<ServiceEnterpriseDraft>()
  useEffect(() => {
    // 随表单挂载后再同步，避免弹窗首次打开前操作未连接的实例。
    form.setFieldsValue(value)
  }, [form, value])
  return null
}
